export const PAIR_IDS = [
  "canola-matif-rapeseed",
  "canola-soybean",
  "canola-soybean-oil",
] as const;

export type PairId = (typeof PAIR_IDS)[number];

export type MappingMode = "auto" | "manual";
export type QualityStatus = "good" | "partial" | "low-quality" | "unavailable";
export type MetricTone = "neutral" | "accent" | "positive" | "negative" | "warning";
export type ApproximateProbabilityBand = "<0.01" | "0.01-0.05" | "0.05-0.10" | ">=0.10";

/** September is intentionally absent from the executable UI universe. */
export type EnabledMonthCode = "F" | "G" | "H" | "K" | "N" | "Q" | "X" | "Z";

export interface ContractChoice {
  readonly id: string;
  /** Canonical contract id understood by the market-data adapter. */
  readonly dataContractId: string;
  readonly exchange: "ICE" | "CBOT" | "MATIF";
  readonly product: "Canola" | "Soybean" | "Soybean Oil" | "Rapeseed";
  readonly monthCode: EnabledMonthCode;
  readonly deliveryMonth: string;
  readonly year: number;
  readonly symbol: string;
  readonly compact: string;
  readonly label: string;
}

export interface ContractPairSelection {
  readonly legA: ContractChoice;
  readonly legB: ContractChoice;
}

export interface DashboardMetric {
  readonly label: string;
  readonly value: string;
  readonly detail?: string;
  readonly tone?: MetricTone;
  readonly unavailableReason?: string;
}

export interface OverviewRow {
  readonly pairId: PairId;
  readonly pairShort: string;
  readonly pairLabel: string;
  readonly primaryMetricLabel: string;
  readonly primaryMetricValue: string;
  readonly seasonalZ: number | null;
  readonly localZ: number | null;
  readonly returnCorrelation: number | null;
  readonly beta: number | null;
  readonly adfStatistic: number | null;
  readonly adfProbabilityBand: ApproximateProbabilityBand | null;
  readonly adfExactPValue: null;
  readonly cointegrationStatistic: number | null;
  readonly cointegrationProbabilityBand: ApproximateProbabilityBand | null;
  readonly cointegrationExactPValue: null;
  readonly stationarityCaveat: string;
  readonly halfLifeDays: number | null;
  readonly halfLifeReason?: string;
  readonly stability: "Stable" | "Mixed" | "Unstable" | "Unknown";
  readonly signal: string;
  readonly quality: QualityStatus;
  readonly qualityDetail: string;
}

export interface MatifDecompositionFixture {
  readonly template: string;
  readonly metrics: readonly DashboardMetric[];
}

export interface SpreadPoint {
  readonly date: string;
  readonly value: number;
  readonly mean: number;
  readonly upperOne: number;
  readonly lowerOne: number;
  readonly upperTwo: number;
  readonly lowerTwo: number;
  readonly zScore: number | null;
  readonly eligible: boolean;
  readonly quality: "good" | "display-only" | "partial";
  readonly marker?: "mapping-start" | "eligible-start" | "entry" | "exit";
}

export interface SeasonalPoint {
  readonly seasonIndex: number;
  readonly seasonLabel: string;
  readonly actualDate: string;
  readonly value: number;
}

export interface SeasonalSeries {
  readonly contractYear: number;
  readonly contracts: string;
  readonly current: boolean;
  readonly points: readonly SeasonalPoint[];
}

export interface SeasonalAveragePoint {
  readonly seasonIndex: number;
  readonly seasonLabel: string;
  readonly average: number;
  readonly median: number;
  readonly p10: number;
  readonly p25: number;
  readonly p75: number;
  readonly p90: number;
  readonly current: number | null;
}

export interface StrategyPoint {
  readonly date: string;
  readonly strategyIndex: number;
  readonly grossIndex: number;
  readonly drawdown: number;
}

export interface ForwardStudyRow {
  readonly horizon: "5d" | "10d" | "20d" | "40d";
  readonly meanChange: number | null;
  readonly medianChange: number | null;
  readonly reversionRate: number | null;
  readonly winRate: number | null;
  readonly mae: number | null;
  readonly mfe: number | null;
  readonly sampleSize: number;
}

export interface RegimePoint {
  readonly date: string;
  readonly correlation: number | null;
  readonly beta: number | null;
  readonly adfStatistic: number | null;
  readonly adfProbabilityBand: ApproximateProbabilityBand | null;
  readonly cointegrationStatistic: number | null;
  readonly cointegrationProbabilityBand: ApproximateProbabilityBand | null;
  readonly halfLife: number | null;
  readonly volatility: number | null;
  readonly localZ: number | null;
  readonly seasonalZ: number | null;
  /** Stability score on the application service's 0–100 scale. */
  readonly betaStability: number | null;
  readonly qualityScore: number;
}

export interface WindowSensitivityRow {
  readonly window: 20 | 40 | 60 | 90 | 120 | 180 | 252;
  readonly correlation: number | null;
  readonly beta: number | null;
  readonly adfStatistic: number | null;
  readonly adfProbabilityBand: ApproximateProbabilityBand | null;
  readonly adfExactPValue: null;
  readonly cointegrationProbabilityBand: ApproximateProbabilityBand | null;
  readonly cointegrationExactPValue: null;
  readonly halfLife: number | null;
  readonly stability: "stable" | "watch" | "overfit-risk";
}

export interface LeadLagRow {
  readonly alignment: string;
  readonly lag: -1 | 0 | 1;
  readonly returnCorrelation: number | null;
  readonly eligibleObservations: number;
  readonly interpretation: string;
}

export interface PairDashboardFixture {
  readonly id: PairId;
  readonly shortLabel: string;
  readonly label: string;
  readonly description: string;
  readonly autoContracts: ContractPairSelection;
  readonly manualLegAChoices: readonly ContractChoice[];
  readonly manualLegBChoices: readonly ContractChoice[];
  readonly overview: OverviewRow;
  readonly spread: {
    readonly points: readonly SpreadPoint[];
    readonly eligibleStart: string;
    readonly displayStart: string;
    readonly metricLabel: string;
    readonly unit: string;
    readonly metrics: readonly DashboardMetric[];
    readonly decompositionByTemplate?: Readonly<Record<string, MatifDecompositionFixture>>;
  };
  readonly seasonality: {
    readonly template: string;
    readonly windowLabel: string;
    readonly currentYear: number;
    readonly series: readonly SeasonalSeries[];
    readonly average: readonly SeasonalAveragePoint[];
  };
  readonly signalLab: {
    readonly strategy: readonly StrategyPoint[];
    readonly metrics: readonly DashboardMetric[];
    readonly forwardStudy: readonly ForwardStudyRow[];
    readonly costAssumption: string;
  };
  readonly regime: {
    readonly rolling: readonly RegimePoint[];
    readonly windows: readonly WindowSensitivityRow[];
    readonly leadLag: readonly LeadLagRow[];
    readonly qualityNotes: readonly string[];
    readonly stationarityCaveat: string;
  };
}

export interface DemoMetadata {
  readonly mode: "demo";
  readonly source: string;
  readonly asOf: string;
  readonly coverage: string;
  readonly generatedAt: string;
}
